import React, { useState } from 'react';
import { Calculator, Copy, Trash2, TrendingUp, BarChart3 } from 'lucide-react';

const CompoundInterestCalculatorTool = () => {
  const [principal, setPrincipal] = useState('');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [rate, setRate] = useState('');
  const [years, setYears] = useState('');
  const [frequency, setFrequency] = useState('12');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const frequencyOptions = [
    { value: '1', label: 'Annually (1x/year)' },
    { value: '2', label: 'Semi-annually (2x/year)' },
    { value: '4', label: 'Quarterly (4x/year)' },
    { value: '12', label: 'Monthly (12x/year)' },
    { value: '52', label: 'Weekly (52x/year)' },
    { value: '365', label: 'Daily (365x/year)' },
  ];

  const calculate = () => {
    const P = parseFloat(principal) || 0;
    const PMT = parseFloat(monthlyContribution) || 0;
    const r = parseFloat(rate) / 100;
    const t = parseFloat(years);
    const n = parseFloat(frequency);

    if ((P <= 0 && PMT <= 0) || isNaN(r) || isNaN(t) || t <= 0 || n <= 0) {
      setResult(null);
      return;
    }

    // Compound Interest with Monthly Contributions
    // A = P(1 + r/n)^(nt) + PMT × [((1 + r/n)^(nt) - 1) / (r/n)]
    const periodicRate = r / n;
    const totalPeriods = n * t;
    
    // Future value of initial principal
    const fvPrincipal = P * Math.pow(1 + periodicRate, totalPeriods);
    
    // Future value of monthly contributions (converted to match compounding frequency)
    // For monthly contributions with different compounding, we need to adjust
    const monthlyToFrequency = 12 / n; // How many months per compounding period
    const contributionPerPeriod = PMT * monthlyToFrequency;
    
    let fvContributions = 0;
    if (periodicRate > 0) {
      fvContributions = contributionPerPeriod * ((Math.pow(1 + periodicRate, totalPeriods) - 1) / periodicRate);
    } else {
      fvContributions = contributionPerPeriod * totalPeriods;
    }
    
    const finalAmount = fvPrincipal + fvContributions;
    const totalContributions = P + (PMT * 12 * t);
    const totalInterest = finalAmount - totalContributions;
    const effectiveRate = (Math.pow(1 + r / n, n) - 1) * 100;

    // Generate yearly growth data for chart
    const yearlyData = [];
    for (let year = 0; year <= t; year++) {
      const periods = n * year;
      const fvP = P * Math.pow(1 + periodicRate, periods);
      let fvC = 0;
      if (periodicRate > 0 && periods > 0) {
        fvC = contributionPerPeriod * ((Math.pow(1 + periodicRate, periods) - 1) / periodicRate);
      } else {
        fvC = contributionPerPeriod * periods;
      }
      const balance = fvP + fvC;
      const contributed = P + (PMT * 12 * year);
      const interest = balance - contributed;
      
      yearlyData.push({
        year,
        balance,
        contributed,
        interest
      });
    }

    setResult({
      principal: P,
      monthlyContribution: PMT,
      rate: r * 100,
      years: t,
      frequency: n,
      frequencyLabel: frequencyOptions.find(f => f.value === frequency)?.label,
      finalAmount,
      totalContributions,
      totalInterest,
      effectiveRate,
      yearlyData
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setPrincipal('');
    setMonthlyContribution('');
    setRate('');
    setYears('');
    setFrequency('12');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Calculate max value for chart scaling
  const maxValue = result ? Math.max(...result.yearlyData.map(d => d.balance)) : 0;

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Initial Investment ($)
            </label>
            <input
              type="number"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              placeholder="e.g., 10000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="principal-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Monthly Contribution ($)
            </label>
            <input
              type="number"
              value={monthlyContribution}
              onChange={(e) => setMonthlyContribution(e.target.value)}
              placeholder="e.g., 500"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="monthly-contribution-input"
            />
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Interest Rate (% per year)
            </label>
            <input
              type="number"
              step="0.01"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g., 7"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Period (Years)
            </label>
            <input
              type="number"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              placeholder="e.g., 10"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="years-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Compounding Frequency
            </label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
              data-testid="frequency-select"
            >
              {frequencyOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-compound-btn"
      >
        <Calculator size={18} />
        Calculate Growth
      </button>

      {/* Results Section */}
      {result && (
        <div className="space-y-4" data-testid="compound-results">
          {/* Main Results */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Final Balance</span>
                  <button
                    onClick={() => copyResult(result.finalAmount, 'final')}
                    className={`p-1.5 rounded transition-colors ${
                      copied === 'final' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    data-testid="copy-final"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="text-3xl font-bold text-green-400" data-testid="final-balance-result">
                  {formatCurrency(result.finalAmount)}
                </div>
              </div>

              <div className="p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Total Interest Earned</span>
                  <button
                    onClick={() => copyResult(result.totalInterest, 'interest')}
                    className={`p-1.5 rounded transition-colors ${
                      copied === 'interest' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    data-testid="copy-interest"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="text-3xl font-bold text-yellow-400" data-testid="total-interest-result">
                  {formatCurrency(result.totalInterest)}
                </div>
              </div>
            </div>
          </div>

          {/* Growth Chart */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={18} className="text-blue-400" />
              <h3 className="text-sm font-medium text-gray-300">Growth Over Time</h3>
            </div>
            <div className="space-y-2">
              {result.yearlyData.map((data, index) => (
                <div key={data.year} className="flex items-center gap-3">
                  <div className="w-12 text-xs text-gray-400 text-right">
                    {data.year === 0 ? 'Start' : `Y${data.year}`}
                  </div>
                  <div className="flex-1 h-6 bg-gray-700 rounded overflow-hidden relative">
                    {/* Contributions bar */}
                    <div 
                      className="absolute left-0 top-0 h-full bg-blue-600 transition-all"
                      style={{ width: `${(data.contributed / maxValue) * 100}%` }}
                    />
                    {/* Interest bar (on top of contributions) */}
                    <div 
                      className="absolute left-0 top-0 h-full bg-green-500 transition-all"
                      style={{ 
                        width: `${(data.balance / maxValue) * 100}%`,
                        clipPath: `inset(0 0 0 ${(data.contributed / data.balance) * 100}%)`
                      }}
                    />
                  </div>
                  <div className="w-28 text-xs text-right text-gray-300">
                    {formatCurrency(data.balance)}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-6 mt-4 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-blue-600 rounded"></span> Contributions
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-green-500 rounded"></span> Interest Earned
              </span>
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Investment Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Initial Investment</span>
                <span className="text-white font-medium">{formatCurrency(result.principal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Monthly Contribution</span>
                <span className="text-white font-medium">{formatCurrency(result.monthlyContribution)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Contributions</span>
                <span className="text-blue-400 font-medium">{formatCurrency(result.totalContributions)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interest Rate</span>
                <span className="text-white font-medium">{result.rate}% per year</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Effective Annual Rate</span>
                <span className="text-white font-medium">{result.effectiveRate.toFixed(2)}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Compounding</span>
                <span className="text-white font-medium">{result.frequencyLabel}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Total Interest Earned</span>
                <span className="text-yellow-400 font-medium">{formatCurrency(result.totalInterest)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Final Balance</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.finalAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Compound Interest Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded space-y-2">
          <div>A = P(1 + r/n)^(nt) + PMT × [((1 + r/n)^(nt) - 1) / (r/n)]</div>
        </div>
        <div className="text-xs text-gray-500 mt-2 space-y-1">
          <div><span className="text-gray-400">A</span> = Final amount</div>
          <div><span className="text-gray-400">P</span> = Principal (initial investment)</div>
          <div><span className="text-gray-400">PMT</span> = Periodic contribution</div>
          <div><span className="text-gray-400">r</span> = Annual interest rate (decimal)</div>
          <div><span className="text-gray-400">n</span> = Compounds per year</div>
          <div><span className="text-gray-400">t</span> = Time in years</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-compound-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default CompoundInterestCalculatorTool;
